import { useEffect, useState, useRef, type CSSProperties } from 'react';
import {
  dirPickerSupported, pickDir, getAllDirNames, clearDir, getHandles, getAccessStates,
  requestAccessAll, pickInboxDir, getInboxDirName, clearInboxDir, getInboxAccess, getInboxHandle,
  requestInboxAccess, type TargetKind, type AccessState,
} from '@/lib/fileTargets';
import {
  pickRecordingDir, getRecordingDirName, clearRecordingDir, getRecordingAccess, requestRecordingAccess,
  getRecordingHandle, getRecipientKey, setRecipientKey, clearRecipientKey, type RecAccess,
} from '@/lib/recordingDelivery';
import { inspectPublicKey } from '@/lib/recordingCrypto';

/**
 * pre158: Ablage- & Ordner-Konfiguration (File System Access API).
 * Aus der Nachbearbeitung (WrapupView) in die Einstellungen verlagert. Setzt je
 * Dokumententyp ein Zielverzeichnis, den Aufzeichnungs-Hot-Folder, den Eingang
 * „zur Unterschrift" und den PGP-Empfängerschlüssel. Die Edge-sensible
 * Gesten-Erhaltung (confirmAccess ohne await vor requestAccess*) bleibt erhalten.
 */
const KIND_DIRS: { kind: TargetKind; label: string }[] = [
  { kind: 'scan', label: 'Scans' },
  { kind: 'signed', label: 'Unterschriebene Verträge' },
  { kind: 'nfc', label: 'EC-Karten-Protokolle' },
];

function kindLabel(k: string): string {
  return k === 'signed' ? 'SIG' : k === 'nfc' ? 'EC' : 'SCAN';
}
function kindTag(kind: string): CSSProperties {
  const base: CSSProperties = { fontSize: 8.5, fontWeight: 700, letterSpacing: '.06em', padding: '2px 5px', borderRadius: 4, flexShrink: 0, width: 34, textAlign: 'center' };
  if (kind === 'signed') return { ...base, color: '#22c55e', background: '#22c55e26' };
  if (kind === 'nfc') return { ...base, color: '#3b82f6', background: '#3b82f626' };
  return { ...base, color: 'var(--muted)', background: 'var(--bg-elev-3)' };
}

const blockLabel: CSSProperties = { fontSize: 10.5, letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--muted)', fontWeight: 600 };
const confirmBar: CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px', background: 'var(--tenant-accent-soft)', border: '1px solid var(--tenant-accent-soft)', borderRadius: 8 };
const dirBar: CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', background: 'var(--bg-elev-1)', border: '1px solid var(--hairline)', borderRadius: 8 };
const miniBtn: CSSProperties = { border: '1px solid var(--hairline)', background: 'var(--bg-elev-2)', borderRadius: 7, padding: '6px 12px', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', color: 'inherit' };

export function FolderTargetsSettings() {
  const dirSupported = dirPickerSupported();
  const [dirNames, setDirNames] = useState<Record<TargetKind, string | null>>({ scan: null, signed: null, nfc: null });
  const [recDir, setRecDir] = useState<string | null>(null);
  const [recAccess, setRecAccess] = useState<RecAccess>('none');
  const [inboxName, setInboxName] = useState<string | null>(null);
  const [inboxAccess, setInboxAccess] = useState<AccessState>('none');
  const inboxHandleRef = useRef<any>(null);
  const recHandleRef = useRef<any | null>(null);
  const [recipFp, setRecipFp] = useState<string | null>(null);
  const [recipOpen, setRecipOpen] = useState(false);
  const [recipDraft, setRecipDraft] = useState('');
  const [recipErr, setRecipErr] = useState<string | null>(null);
  const [access, setAccess] = useState<Record<TargetKind, AccessState>>({ scan: 'none', signed: 'none', nfc: 'none' });
  const handlesRef = useRef<Record<TargetKind, any | null>>({ scan: null, signed: null, nfc: null });

  async function loadRecipient() {
    const k = await getRecipientKey();
    if (!k) { setRecipFp(null); return; }
    const info = await inspectPublicKey(k);
    setRecipFp(info.ok ? (info.fingerprint ?? 'gesetzt') : 'ungültig');
  }
  useEffect(() => { loadRecipient().catch(() => { /* */ }); }, []);
  async function saveRecipient() {
    const armored = recipDraft.trim();
    if (!armored) { setRecipErr('Bitte einen PGP-Public-Key einfügen.'); return; }
    const info = await inspectPublicKey(armored);
    if (!info.ok) { setRecipErr('Kein gültiger OpenPGP-Public-Key.'); return; }
    await setRecipientKey(armored);
    setRecipErr(null); setRecipOpen(false); setRecipDraft('');
    await loadRecipient();
  }
  async function removeRecipient() { await clearRecipientKey(); setRecipFp(null); }
  async function refreshRec() {
    const [name, access, handle] = await Promise.all([getRecordingDirName(), getRecordingAccess(), getRecordingHandle()]);
    setRecDir(name); setRecAccess(access); recHandleRef.current = handle;
  }
  async function refreshInbox() {
    const [iname, ihandle] = await Promise.all([getInboxDirName(), getInboxHandle()]);
    setInboxName(iname); inboxHandleRef.current = ihandle;
    setInboxAccess(ihandle ? await getInboxAccess(ihandle) : 'none');
  }
  useEffect(() => { refreshInbox().catch(() => {}); }, []);
  useEffect(() => { refreshRec().catch(() => { /* */ }); }, []);
  async function refreshTargets() {
    const [names, handles] = await Promise.all([getAllDirNames(), getHandles()]);
    handlesRef.current = handles;
    setDirNames(names);
    setAccess(await getAccessStates(handles));
  }
  useEffect(() => { if (dirSupported) refreshTargets().catch(() => { /* */ }); }, [dirSupported]);
  async function chooseDir(kind: TargetKind) {
    try { const name = await pickDir(kind); if (name) await refreshTargets(); } catch { /* abgebrochen */ }
  }
  async function removeDir(kind: TargetKind) { await clearDir(kind).catch(() => { /* */ }); await refreshTargets(); }
  // EIN Klick → Berechtigung für alle gemerkten Ordner neu anfragen (synchron, Geste erhalten).
  function confirmAccess() {
    requestAccessAll(handlesRef.current).then(() => getAccessStates(handlesRef.current)).then(setAccess).catch(() => { /* */ });
    // REC-Ordner im selben Gesten-Kontext mit anfragen (Handle ist gecacht → kein await davor).
    if (recHandleRef.current) {
      requestRecordingAccess(recHandleRef.current).then(setRecAccess).catch(() => { /* */ });
    }
  }
  const needsConfirm = (['scan', 'signed', 'nfc'] as TargetKind[]).some((k) => dirNames[k] && access[k] !== 'granted')
    || (!!recDir && recAccess !== 'granted');

  if (!dirSupported) {
    return (
      <div style={{ ...dirBar, color: 'var(--muted)', fontSize: 12.5 }}>
        Dieser Browser unterstützt keine direkte Ordner-Ablage (File System Access API). Dokumente werden per „Speichern unter …" abgelegt.
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ ...blockLabel, marginBottom: 2 }}>Zielordner je Dokumententyp · Ablage ohne Dialog</div>

      {needsConfirm && (
        <div style={confirmBar}>
          <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, lineHeight: 1.45 }}>
            🔓 Der Ordnerzugriff muss nach Browser-/Sitzungsstart einmal bestätigt werden. Die Ordner bleiben gemerkt — ein Klick genügt.
          </span>
          <button onClick={confirmAccess} style={{ ...miniBtn, background: 'var(--tenant-accent)', color: '#fff', borderColor: 'var(--tenant-accent)' }}>
            Ordnerzugriff bestätigen
          </button>
        </div>
      )}

      {KIND_DIRS.map(({ kind, label }) => (
        <div key={kind} style={dirBar}>
          <span style={kindTag(kind)}>{kindLabel(kind)}</span>
          <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {label}:{' '}
            {dirNames[kind]
              ? <strong style={{ color: 'var(--fg)' }}>{dirNames[kind]}</strong>
              : <span style={{ color: 'var(--muted)' }}>nicht gewählt</span>}
          </span>
          {dirNames[kind] && (
            access[kind] === 'granted'
              ? <span style={{ fontSize: 10.5, color: 'var(--status-available)', fontWeight: 700, flexShrink: 0 }} title="Zugriff aktiv">● aktiv</span>
              : <span style={{ fontSize: 10.5, color: '#d8a23a', fontWeight: 700, flexShrink: 0 }} title="Zugriff muss bestätigt werden">● zu bestätigen</span>
          )}
          <button onClick={() => chooseDir(kind)} style={miniBtn}>{dirNames[kind] ? 'Ändern' : 'Ordner wählen'}</button>
          {dirNames[kind] && <button onClick={() => removeDir(kind)} style={{ ...miniBtn, color: '#DC2626' }}>Zurücksetzen</button>}
        </div>
      ))}

      <div style={dirBar}>
        <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: 'var(--muted)', color: '#fff', flexShrink: 0 }}>REC</span>
        <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          Aufzeichnungen (Zielordner / FI-Hot-Folder):{' '}
          {recDir
            ? <strong style={{ color: 'var(--fg)' }}>{recDir}</strong>
            : <span style={{ color: 'var(--muted)' }}>nicht gewählt — sonst „Speichern unter …"</span>}
        </span>
        {recDir && (
          recAccess === 'granted'
            ? <span style={{ fontSize: 10.5, color: 'var(--status-available)', fontWeight: 700, flexShrink: 0 }} title="Zugriff aktiv">● aktiv</span>
            : <span style={{ fontSize: 10.5, color: '#d8a23a', fontWeight: 700, flexShrink: 0 }} title="Zugriff muss bestätigt werden">● zu bestätigen</span>
        )}
        <button onClick={async () => { try { const n = await pickRecordingDir(); if (n) await refreshRec(); } catch { /* abgebrochen */ } }} style={miniBtn}>{recDir ? 'Ändern' : 'Ordner wählen'}</button>
        {recDir && <button onClick={async () => { await clearRecordingDir(); await refreshRec(); }} style={{ ...miniBtn, color: '#DC2626' }}>Zurücksetzen</button>}
      </div>

      <div style={dirBar}>
        <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: '#0f766e', color: '#fff', flexShrink: 0 }}>IN</span>
        <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          Eingang „zur Unterschrift" (überwacht):{' '}
          {inboxName
            ? <strong style={{ color: 'var(--fg)' }}>{inboxName}</strong>
            : <span style={{ color: 'var(--muted)' }}>nicht gewählt</span>}
        </span>
        {inboxName && (
          inboxAccess === 'granted'
            ? <span style={{ fontSize: 10.5, color: 'var(--status-available)', fontWeight: 700, flexShrink: 0 }} title="Zugriff aktiv">● aktiv</span>
            : <span style={{ fontSize: 10.5, color: '#d8a23a', fontWeight: 700, flexShrink: 0 }} title="Zugriff muss bestätigt werden">● zu bestätigen</span>
        )}
        {inboxName && inboxAccess !== 'granted' && (
          <button onClick={async () => { if (inboxHandleRef.current) { await requestInboxAccess(inboxHandleRef.current); await refreshInbox(); } }} style={{ ...miniBtn, background: 'var(--tenant-accent)', color: '#fff', borderColor: 'var(--tenant-accent)' }}>Bestätigen</button>
        )}
        <button onClick={async () => { try { const n = await pickInboxDir(); if (n) await refreshInbox(); } catch { /* abgebrochen */ } }} style={miniBtn}>{inboxName ? 'Ändern' : 'Ordner wählen'}</button>
        {inboxName && <button onClick={async () => { await clearInboxDir(); await refreshInbox(); }} style={{ ...miniBtn, color: '#DC2626' }}>Zurücksetzen</button>}
      </div>

      <div style={dirBar}>
        <span style={{ fontSize: 10.5, fontWeight: 700, padding: '2px 6px', borderRadius: 4, background: '#6d28d9', color: '#fff', flexShrink: 0 }}>PGP</span>
        <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          Empfänger-Schlüssel (verschlüsselt die Ablage):{' '}
          {recipFp
            ? (recipFp === 'ungültig'
                ? <strong style={{ color: '#DC2626' }}>● ungültiger Schlüssel</strong>
                : <strong style={{ color: 'var(--fg)' }}>● gesetzt · …{recipFp.slice(-16)}</strong>)
            : <span style={{ color: '#d8a23a' }}>kein Schlüssel — Ablage erfolgt UNVERSCHLÜSSELT</span>}
        </span>
        <button onClick={() => { setRecipOpen((o) => !o); setRecipErr(null); }} style={miniBtn}>{recipFp ? 'Ändern' : 'Schlüssel einfügen'}</button>
        {recipFp && <button onClick={removeRecipient} style={{ ...miniBtn, color: '#DC2626' }}>Entfernen</button>}
      </div>
      {recipOpen && (
        <div>
          <textarea value={recipDraft} onChange={(e) => setRecipDraft(e.target.value)}
            placeholder={'-----BEGIN PGP PUBLIC KEY BLOCK-----\n…\n-----END PGP PUBLIC KEY BLOCK-----'}
            style={{ width: '100%', minHeight: 110, fontFamily: 'monospace', fontSize: 11.5, padding: 8, borderRadius: 8, border: '1px solid var(--hairline)', resize: 'vertical', boxSizing: 'border-box', background: 'var(--bg-elev-2)', color: 'var(--fg)' }} />
          {recipErr && <div style={{ color: '#DC2626', fontSize: 12, marginTop: 4 }}>{recipErr}</div>}
          <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
            <button onClick={saveRecipient} style={miniBtn}>Speichern</button>
            <button onClick={() => { setRecipOpen(false); setRecipDraft(''); setRecipErr(null); }} style={miniBtn}>Abbrechen</button>
          </div>
        </div>
      )}
    </div>
  );
}
